'use client';

import { useEffect, useRef } from 'react';
import { resolvePath, resolveText, setPathValue } from '@macaron-a2ui/core/v0_8';
import { resolveActionContext } from './helpers';
import type { MacaronRenderContext, RecordAny } from '@macaron-a2ui/core/v0_8';

const PASSWORD_KEYPAD_LENGTH = 6;
const PASSWORD_KEY_ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['DEL', '0', 'ENT'],
] as const;

function normalizePasswordValue(value: string) {
  return value.replace(/\D/g, '').slice(0, PASSWORD_KEYPAD_LENGTH);
}

function dispatchPasswordAction(ctx: MacaronRenderContext, action: RecordAny | undefined, valuePath: string | undefined, value: string, trigger: 'auto' | 'enter') {
  if (typeof action?.name !== 'string' || !action.name) return;
  const actionData = valuePath ? setPathValue(ctx.data, valuePath, value) : ctx.data;
  ctx.onAction?.(action.name, ctx.componentId, { ...resolveActionContext(action, actionData), value, length: value.length, trigger });
}

export function MacaronPasswordKeypad({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const valuePath = resolvePath(config.value);
  const rawValue = resolveText(config.value, ctx.data);
  const value = normalizePasswordValue(rawValue);
  const action = (config.action as RecordAny | undefined) || undefined;
  const didClearInitialValue = useRef(false);
  const updateData = ctx.updateData;

  useEffect(() => {
    if (didClearInitialValue.current) return;
    didClearInitialValue.current = true;
    if (valuePath && value) updateData?.(valuePath, '');
  }, [updateData, value, valuePath]);

  useEffect(() => {
    if (!didClearInitialValue.current || !valuePath || rawValue === value) return;
    updateData?.(valuePath, value);
  }, [rawValue, updateData, value, valuePath]);

  const effectiveValue = didClearInitialValue.current ? value : '';

  return (
    <div className="w-full rounded-xl border border-black/10 bg-white p-3">
      <div className="mb-3 flex min-h-20 items-center rounded-[10px] bg-[#F7F3ED] px-7 py-6">
        {Array.from({ length: PASSWORD_KEYPAD_LENGTH }).map((_, index) => (
          <div key={index} className="flex-1 text-center text-2xl font-medium tracking-[0.08em] text-[#161615]">
            {index < effectiveValue.length ? effectiveValue[index] : '_'}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {PASSWORD_KEY_ROWS.flat().map((key) => (
          <button
            key={key}
            type="button"
            className="min-h-12 rounded-xl border border-[#D0D5DD] bg-white text-base font-semibold text-[#161615] shadow-[0_2px_10px_rgba(22,22,21,0.04)]"
            onClick={() => {
              if (!valuePath) return;
              if (key === 'DEL') {
                if (!effectiveValue) return;
                ctx.updateData?.(valuePath, effectiveValue.slice(0, -1));
                return;
              }
              if (key === 'ENT') {
                if (!effectiveValue) return;
                dispatchPasswordAction(ctx, action, valuePath, effectiveValue, 'enter');
                return;
              }
              if (effectiveValue.length >= PASSWORD_KEYPAD_LENGTH) return;
              const nextValue = `${effectiveValue}${key}`;
              ctx.updateData?.(valuePath, nextValue);
              if (nextValue.length === PASSWORD_KEYPAD_LENGTH) dispatchPasswordAction(ctx, action, valuePath, nextValue, 'auto');
            }}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}
